import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Checkbox,
  Slide,
} from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import { Search as SearchIcon } from "@mui/icons-material";
import { MonitorGroup, ServiceStatusAggregation } from "../../Models/Types";

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement<any, any> },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export const matchesAppSearch = (
  agg: ServiceStatusAggregation,
  search: string,
): boolean => {
  const s = search.toLowerCase();
  return (
    agg.application.toLowerCase().includes(s) ||
    agg.items.some((item) =>
      (item.responsible || "").toLowerCase().includes(s),
    )
  );
};

interface GroupModalProps {
  open: boolean;
  onClose: () => void;
  // Updated onSave signature to include threshold
  onSave: (name: string, selectedIds: number[], threshold: number) => void;
  onDelete: () => void;
  editingGroup: MonitorGroup | null;
  aggregatedData: ServiceStatusAggregation[];
}

const GroupModal: React.FC<GroupModalProps> = ({
  open,
  onClose,
  onSave,
  onDelete,
  editingGroup,
  aggregatedData,
}) => {
  const [groupNameInput, setGroupNameInput] = useState("");
  const [modalSearchText, setModalSearchText] = useState("");

  // New state for the group threshold
  const [groupThresholdInput, setGroupThresholdInput] = useState<number | "">(
    10,
  );

  // We still store individual Service IDs because the backend API expects them
  const [selectedServicesInGroup, setSelectedServicesInGroup] = useState<
    number[]
  >([]);

  useEffect(() => {
    if (open) {
      setModalSearchText("");
      if (editingGroup) {
        setGroupNameInput(editingGroup.groupName);
        setSelectedServicesInGroup(
          editingGroup.applicationIdsInGroup
            ? editingGroup.applicationIdsInGroup.split(",").map(Number)
            : [],
        );
        // Load existing threshold or fallback to default 10
        setGroupThresholdInput(
          editingGroup.threshold !== undefined ? editingGroup.threshold : 10,
        );
      } else {
        setGroupNameInput("");
        setSelectedServicesInGroup([]);
        setGroupThresholdInput(10); // Default to 10 for new groups
      }
    }
  }, [open, editingGroup]);

  const handleSaveClick = () => {
    // Ensure we pass a valid number, fallback to 0 if something bypasses the disabled button
    const finalThreshold = groupThresholdInput === "" ? 0 : groupThresholdInput;
    onSave(groupNameInput, selectedServicesInGroup, finalThreshold);
  };
  
  const handleAppToggle = (agg: ServiceStatusAggregation) => {
    const id = agg.application_id;
    var currentIds = [...selectedServicesInGroup];

    if (currentIds.includes(id)) {
      setSelectedServicesInGroup(currentIds.filter((id_) => id_ !== id));
    } else {
      currentIds.push(id);
      setSelectedServicesInGroup(currentIds);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullScreen
      TransitionComponent={Transition}
      PaperProps={{
        sx: {
          height: "98dvh",
          maxHeight: "98dvh",
          m: 0,
          borderRadius: 0,
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <DialogContent dividers sx={{ padding: 0 }}>
        <div className="section-container">
          <Typography className="section-title" sx={{ pt: 2 }}>
            {editingGroup ? "Modifica gruppo" : "Crea nuovo gruppo"}
          </Typography>

          <TextField
            autoFocus
            margin="dense"
            label="Nome del gruppo"
            fullWidth
            variant="filled"
            value={groupNameInput}
            inputProps={{ maxLength: 30 }}
            helperText={`${groupNameInput.length} / 30 caratteri`}
            FormHelperTextProps={{ sx: { ml: 2, mt: 0.5, textAlign: "right" } }}
            onChange={(e) => setGroupNameInput(e.target.value)}
            sx={{
              "& .MuiFilledInput-root": { backgroundColor: "transparent" },
            }}
          />

          {/* New Threshold Input Field */}
          <TextField
            margin="dense"
            label="Soglia di allarme (%)"
            type="number"
            required // Visually marks the field as required
            fullWidth
            variant="filled"
            value={groupThresholdInput}
            inputProps={{ min: 0, max: 100 }}
            helperText="Ricevi una notifica push se la percentuale di App KO supera questo valore."
            FormHelperTextProps={{ sx: { ml: 2, mt: 0.5 } }}
            onChange={(e) => {
              const rawValue = e.target.value;

              // Allow the field to be temporarily empty
              if (rawValue === "") {
                setGroupThresholdInput("");
                return;
              }

              const val = parseInt(rawValue, 10);
              if (!isNaN(val)) {
                // Enforce 0-100 boundaries on the fly
                const clampedVal = Math.max(0, Math.min(100, val));
                setGroupThresholdInput(clampedVal);
              }
            }}
            sx={{
              marginTop: 1,
              "& .MuiFilledInput-root": { backgroundColor: "transparent" },
            }}
          />
        </div>

        <div className="section-container bg-gray">
          <Typography className="section-title">Lista applicazioni</Typography>
          <Typography className="section-subtitle">
            Digita almeno due caratteri per visualizzare i risultati.
          </Typography>

          <TextField
            placeholder="Prodotto o referente"
            variant="filled"
            size="small"
            className="search-input"
            value={modalSearchText}
            onChange={(e) => setModalSearchText(e.target.value)}
            InputProps={{
              startAdornment: (
                <SearchIcon color="action" sx={{ mt: "0.5em", mr: 1 }} />
              ),
            }}
            sx={{
              marginBottom: "1em",
              width: "100%",
              "& .MuiFilledInput-root": { backgroundColor: "transparent" },
            }}
          />
          <div className="services-list-modal">
            {aggregatedData
              .filter((agg) => {
                if (selectedServicesInGroup.includes(agg.application_id)) {
                  return true;
                }

                const search = modalSearchText.toLowerCase();
                // Basic search on App name
                if (!search || search.length < 2) return false;
                return matchesAppSearch(agg, search);
              })
              .sort((a, b) =>
                a.application.trim().localeCompare(b.application.trim()),
              )
              .map((agg) => {
                return (
                  <div
                    key={agg.application_id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "1em",
                      padding: "8px 0",
                      borderBottom: "1px solid #eee",
                    }}
                    onClick={() => handleAppToggle(agg)}
                  >
                    <div>
                      <Checkbox
                        checked={selectedServicesInGroup.includes(
                          agg.application_id,
                        )}
                      />
                    </div>
                    <div>
                      <div style={{ fontWeight: 500 }}>{agg.application}</div>
                      <div style={{ fontSize: "0.8em", color: "#666" }}>
                        {agg.items.length} monitors
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      </DialogContent>
      <DialogActions>
        {editingGroup && (
          <Button onClick={onDelete} color="error" sx={{ mr: "auto" }}>
            Elimina
          </Button>
        )}
        <Button onClick={onClose}>Annulla</Button>
        <Button
          onClick={handleSaveClick}
          variant="contained"
          disabled={
            !groupNameInput ||
            selectedServicesInGroup.length === 0 ||
            groupThresholdInput === ""
          }
        >
          Salva
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GroupModal;
